<template>
  <div class="v2-benefits v2-container v2-section">
    <header class="v2-benefits__header">
      <div class="v2-benefits__title-block">
        <p class="v2-overline">Membresía</p>
        <h1 class="v2-headline v2-headline--display-lg v2-benefits__title">
          Mis Beneficios
        </h1>
        <p class="v2-body v2-body--md v2-body--muted">
          Aprovecha las promociones, descuentos y convenios que tenemos para ti como
          miembro de tu cámara.
        </p>
      </div>
    </header>

    <div
      v-if="locked"
      class="v2-benefits__locked"
      role="region"
      aria-label="Beneficios bloqueados"
    >
      <v-icon size="48" color="primary">mdi-lock-outline</v-icon>
      <h2 class="v2-headline v2-headline--display-md">Beneficios bloqueados</h2>
      <p class="v2-body v2-body--md v2-body--muted">
        Los beneficios están disponibles solo para miembros activos. Renueva tu
        membresía para acceder a todas las promociones y convenios.
      </p>
      <BaseButton variant="amber" icon="mdi-refresh" to="/member/renew">
        Renovar membresía
      </BaseButton>
    </div>

    <template v-else>
      <div class="v2-benefits__toolbar">
        <BaseInput
          v-model="search"
          type="search"
          placeholder="Buscar por nombre o descripción…"
          class="v2-benefits__search"
        />
        <p
          v-if="!loading && !error"
          class="v2-body v2-body--sm v2-body--muted v2-benefits__count"
        >
          <template v-if="filteredBenefits.length === 0">
            Sin beneficios que coincidan con tu búsqueda
          </template>
          <template v-else>
            Mostrando {{ filteredBenefits.length }} de {{ totalBenefits }}
            {{ totalBenefits === 1 ? "beneficio" : "beneficios" }}
          </template>
        </p>
      </div>

      <div v-if="loading" class="v2-benefits__loading">
        <v-icon size="32" color="grey lighten-1">mdi-progress-clock</v-icon>
        <p class="v2-body v2-body--sm v2-body--muted">Cargando tus beneficios…</p>
      </div>

      <div v-else-if="error" class="v2-benefits__error">
        <EmptyState
          title="No se pudieron cargar tus beneficios"
          :description="error"
          icon="mdi-cloud-off-outline"
        >
          <BaseButton variant="primary" icon="mdi-refresh" @click="loadBenefits">
            Reintentar
          </BaseButton>
        </EmptyState>
      </div>

      <div
        v-else-if="filteredBenefits.length === 0 && allBenefits.length === 0"
        class="v2-benefits__empty"
      >
        <EmptyState
          title="Aún no tienes beneficios asignados"
          description="Cuando tu cámara publique beneficios para tu categoría, aparecerán aquí."
          icon="mdi-gift-outline"
        />
      </div>

      <ul
        v-else-if="filteredBenefits.length > 0"
        class="v2-benefits__grid"
      >
        <li
          v-for="item in filteredBenefits"
          :key="getBenefitId(item) || getBenefitName(item)"
          class="v2-benefits__grid-item"
        >
          <BenefitCard
            :benefit="item"
            :loading="openingId === getBenefitId(item)"
            @action="onBenefitAction"
          />
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapMutations, mapState } from "vuex";
import BaseButton from "@/components/memberDashboardV2/ui/BaseButton.vue";
import BaseInput from "@/components/memberDashboardV2/ui/BaseInput.vue";
import EmptyState from "@/components/memberDashboardV2/ui/EmptyState.vue";
import BenefitCard from "./BenefitCard.vue";
import { isOrganization } from "@/share/validateOrganization";
import {
  applyBenefitSearch,
  getBenefitId,
  getBenefitUrl,
  isApiBenefit,
  sortBenefitsByName,
} from "./benefitsHelpers";

const MICA_BENEFIT = {
  _id: "__mica__",
  name: "MICA",
  description:
    "Protección de renta con arbitraje y respaldo financiero ante impago. 50% de descuento en investigación integral de inquilinos. Comisión del 20% para asociados.",
  icon: "https://wechamber-v3-files-prod-2.s3.us-east-2.amazonaws.com/sponsor/mica.png",
  benefitType: "api",
};

export default {
  name: "MemberDashboardV2Benefits",
  components: { BaseButton, BaseInput, EmptyState, BenefitCard },
  data() {
    return {
      loading: false,
      error: null,
      allBenefits: [],
      search: "",
      openingId: null,
    };
  },
  computed: {
    ...mapState("user", ["user"]),
    locked() {
      if (!this.user) return true;
      const status = this.user.validationStatus;
      return status === "EXPIRED" || status === "INACTIVO";
    },
    isAmpiUser() {
      if (!this.user) return false;
      return isOrganization(
        "ampi",
        this.user._id,
        this.user.headOffice && this.user.headOffice._id
      );
    },
    showMicaBenefit() {
      if (this.locked) return false;
      if (!this.isAmpiUser) return false;
      return this.allBenefits.length > 0;
    },
    sortedBenefits() {
      const list = [...this.allBenefits];
      if (this.showMicaBenefit) {
        list.unshift(MICA_BENEFIT);
      }
      return sortBenefitsByName(list);
    },
    filteredBenefits() {
      return applyBenefitSearch(this.sortedBenefits, this.search);
    },
    totalBenefits() {
      return this.sortedBenefits.length;
    },
  },
  methods: {
    ...mapActions("benefits", ["getUserBenefits"]),
    ...mapMutations("notification", ["show"]),
    getBenefitId,
    async loadBenefits() {
      this.loading = true;
      this.error = null;
      try {
        const response = await this.getUserBenefits();
        const data = response && response.data;
        const list = data && Array.isArray(data.benefits) ? data.benefits : [];
        this.allBenefits = list;
      } catch (e) {
        this.allBenefits = [];
        this.error =
          "Hubo un problema al obtener los beneficios. Verifica tu conexión e intenta nuevamente.";
        this.show({
          text: "No se pudieron cargar los beneficios",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    async openDirectUrl(url) {
      if (!url) return;
      window.open(url, "_blank", "noopener,noreferrer");
    },
    async openApiBenefit(benefit) {
      const id = getBenefitId(benefit);
      this.openingId = id;
      try {
        const response = await Vue.axios({
          method: "GET",
          url: "/users/ampi/benefits/mica",
        });
        const redirectUrl = response && response.data && response.data.url;
        if (redirectUrl) {
          window.open(redirectUrl, "_blank", "noopener,noreferrer");
        } else {
          this.show({
            text: "No se pudo generar el enlace. Intenta nuevamente.",
            color: "warning",
          });
        }
      } catch (e) {
        this.show({
          text: "No se pudo obtener el beneficio. Intenta nuevamente.",
          color: "error",
        });
      } finally {
        this.openingId = null;
      }
    },
    onBenefitAction({ benefit }) {
      if (this.locked) return;
      if (isApiBenefit(benefit)) {
        this.openApiBenefit(benefit);
        return;
      }
      const url = getBenefitUrl(benefit);
      if (url) {
        this.openDirectUrl(url);
      }
    },
  },
  async mounted() {
    if (this.locked) return;
    await this.loadBenefits();
  },
};
</script>

<style scoped>
.v2-benefits {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.v2-benefits__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--space-4);
  flex-wrap: wrap;
}

.v2-benefits__title-block {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
  flex: 1 1 auto;
}

.v2-benefits__title {
  margin: 0;
}

.v2-benefits__locked {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-12) var(--space-6);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  text-align: center;
}

.v2-benefits__toolbar {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.v2-benefits__search {
  max-width: 480px;
  width: 100%;
}

.v2-benefits__count {
  margin: 0;
}

.v2-benefits__loading,
.v2-benefits__error,
.v2-benefits__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-10) var(--space-4);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
}

.v2-benefits__error,
.v2-benefits__empty {
  text-align: center;
}

.v2-benefits__grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-4);
}

@media (min-width: 600px) {
  .v2-benefits__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .v2-benefits__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.v2-benefits__grid-item {
  display: flex;
  min-width: 0;
}

.v2-benefits__grid-item > * {
  flex: 1 1 auto;
  width: 100%;
}
</style>
